import Swal from "sweetalert2";
import { getAuthUser } from "../../../lib/auth";
import { useState, useEffect, useCallback } from "react";
import { getCustomers, exportCustomers } from "../api/customers.api";

export function useCustomers() {
    const [list, setList] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 10;

    const initialFilters = {
        search: "",
        document: "",
        email: "",
        firstName: "",
        lastName: "",
        hasEmail: false,
    };

    const [filters, setFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);

    const [authUser, setAuthUser] = useState<any>(null);

    useEffect(() => {
        setAuthUser(getAuthUser());
    }, []);

    const fetchCustomers = useCallback(async (page: number, currentFilters: typeof appliedFilters) => {
        setIsLoading(true);
        try {
            const queryParams: any = {
                page,
                limit: perPage,
            };

            // Add filters to query params if they exist
            if (currentFilters.firstName) queryParams['filters[firstName]'] = currentFilters.firstName;
            if (currentFilters.lastName) queryParams['filters[lastName]'] = currentFilters.lastName;
            if (currentFilters.email) queryParams['filters[email]'] = currentFilters.email;
            if (currentFilters.document) queryParams['filters[document]'] = currentFilters.document;
            if (currentFilters.search) queryParams.search = currentFilters.search;
            if (currentFilters.hasEmail) queryParams['filters[hasEmail]'] = true;

            const response: any = await getCustomers(queryParams);
            setList(response?.data || []);
            setTotal(response?.meta?.total || 0);
        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            setIsLoading(false);
        }
    }, [perPage]);

    useEffect(() => {
        fetchCustomers(currentPage, appliedFilters);
    }, [currentPage, appliedFilters, fetchCustomers]);

    const handleFilterChange = (name: string, value: any) => {
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => {
        setAppliedFilters(filters);
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters(initialFilters);
        setAppliedFilters(initialFilters);
        setCurrentPage(1);
    };

    const handlePageChange = (pageIndex: number) => {
        setCurrentPage(pageIndex + 1);
    };

    const handleExport = async (exportType: 'filtered' | 'all') => {
        let criteriaPayload: any = { filters: {} };
        let confirmationText = exportType === 'filtered'
            ? 'Se exportarán los clientes que coincidan con los filtros actuales.'
            : 'Se exportarán todos los clientes del sistema.';

        if (exportType === 'filtered') {
            if (appliedFilters.firstName) criteriaPayload.filters.firstName = appliedFilters.firstName;
            if (appliedFilters.lastName) criteriaPayload.filters.lastName = appliedFilters.lastName;
            if (appliedFilters.email) criteriaPayload.filters.email = appliedFilters.email;
            if (appliedFilters.document) criteriaPayload.filters.document = appliedFilters.document;
            if (appliedFilters.search) criteriaPayload.search = appliedFilters.search;
            if (appliedFilters.hasEmail) criteriaPayload.filters.hasEmail = true;
        }

        const result = await Swal.fire({
            title: '¿Deseas exportar los clientes?',
            text: confirmationText,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Continuar',
            cancelButtonText: 'Cancelar',
        });

        if (result.isConfirmed) {
            try {
                const blob = await exportCustomers(criteriaPayload);
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "clientes.xlsx";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);

                await Swal.fire({
                    title: '¡Exportación exitosa!',
                    text: 'Se exportaron correctamente los clientes.',
                    icon: 'success',
                    confirmButtonText: 'Aceptar'
                });
            } catch (error) {
                await Swal.fire({
                    title: 'Error',
                    text: 'Hubo un problema al exportar los clientes.',
                    icon: 'error',
                    confirmButtonText: 'Cerrar'
                });
            }
        }
    };

    const isAdmin = authUser?.roles?.some((role: any) => role.name === 'Administrador') || false;

    const forceApplyHasEmail = (value: boolean) => {
        setAppliedFilters(prev => ({ ...prev, hasEmail: value }));
        setFilters(prev => ({ ...prev, hasEmail: value }));
        setCurrentPage(1);
    };

    return {
        list,
        total,
        isLoading,
        currentPage,
        filters,
        appliedFilters,
        handleFilterChange,
        applyFilters,
        clearFilters,
        handlePageChange,
        handleExport,
        isAdmin,
        forceApplyHasEmail,
    };
}
